import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { EmergencyCall } from './emergencyCalls.types';
import type { StateWithEmergencyCalls } from './emergencyCallsSlice';

const selectSelectedCallerID = (state: StateWithEmergencyCalls) =>
  state.emergencyCalls.selectedCallerID;

/**
 * Hook to get the selected caller ID from the emergency calls state.
 */
export const useSelectedCallerID = () => useSelector(selectSelectedCallerID);

const selectSelectedCall = (
  state: StateWithEmergencyCalls,
): EmergencyCall | undefined => {
  const callerID = state.emergencyCalls.selectedCallerID;
  if (!callerID) return undefined;
  return state.emergencyCalls.callQueue[callerID];
};

/**
 * Hook to get the selected call from the emergency calls state.
 * Returns the full EmergencyCall object for the currently selected caller ID.
 */
export const useSelectedCall = () => useSelector(selectSelectedCall);

const selectCallByCallerID = createSelector(
  // input selectors:
  [
    (state: StateWithEmergencyCalls) => state.emergencyCalls.callQueue,
    (_, callerID: string) => callerID,
  ],
  // output selector:
  (callQueue, callerID) => (callerID in callQueue ? callQueue[callerID] : null),
);

/**
 * A hook to get the call object by caller ID.
 * @param callerID - The caller ID of the call to retrieve.
 * @returns The EmergencyCall object for the given caller ID.
 */
export const useCallByCallerID = (callerID: string) => {
  return useSelector(state => selectCallByCallerID(state, callerID));
};
